// src/utils/ledger/generatePremiumLedger.js
// ============================================================
//  PREMIUM TIER — Internal Advance Ledger Report
//  Use for: enterprise/govt clients, board reports, tenders.
//  4 pages: Cover, Executive Summary, Detail, Aging+Audit.
// ============================================================
import jsPDF from 'jspdf';
import {
  BRAND, rgb, fmtINR, fmtINRSigned, fmtLakhs, fmtDate, fmtDateTime,
  STATUS_STYLES, computeSummary, groupByClient, groupByMode,
  computeAging, computeDocHash,
  roundRectFill, roundRectStroke, roundRectFS,
  drawStatusPill, drawSparkline, drawDiamond,
  PREMIUM_LAYOUT, stampAllPages,
} from './shared';
import { PdfLayoutEngine, TABLE_METRICS } from './pdfLayoutEngine';

// ============================================================
//  COVER PAGE
// ============================================================
const drawCoverPage = (doc, reportData) => {
  const w = doc.internal.pageSize.getWidth();
  const h = doc.internal.pageSize.getHeight();

  // Dark background
  doc.setFillColor(...rgb(BRAND.COVER_BG));
  doc.rect(0, 0, w, h, 'F');

  // Subtle line texture
  doc.setDrawColor(31, 27, 18);
  doc.setLineWidth(0.1);
  for (let i = 0; i < h; i += 2.5) {
    doc.line(0, i, w, i + 1);
  }

  // Corner ornaments
  doc.setDrawColor(...rgb(BRAND.COVER_GOLD));
  doc.setLineWidth(0.5);
  // top-left
  doc.line(15, 15, 15, 32);
  doc.line(15, 15, 32, 15);
  // top-right
  doc.line(w - 15, 15, w - 15, 32);
  doc.line(w - 15, 15, w - 32, 15);
  // bottom-left
  doc.line(15, h - 15, 15, h - 32);
  doc.line(15, h - 15, 32, h - 15);
  // bottom-right
  doc.line(w - 15, h - 15, w - 15, h - 32);
  doc.line(w - 15, h - 15, w - 32, h - 15);

  // Emblem (circular)
  const emblemY = 60;
  doc.setDrawColor(...rgb(BRAND.COVER_GOLD));
  doc.setLineWidth(0.6);
  doc.circle(w / 2, emblemY, 18, 'S');
  doc.setLineWidth(0.25);
  doc.circle(w / 2, emblemY, 14, 'S');
  doc.setTextColor(...rgb(BRAND.COVER_GOLD));
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.text('DE', w / 2, emblemY + 2, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(5.5);
  doc.text('EST. 2024', w / 2, emblemY + 8, { align: 'center' });

  // Center band
  const bandY = h - 175;
  doc.setFillColor(...rgb(BRAND.COVER_GOLD));
  doc.rect(0, bandY, w, 0.3, 'F');
  doc.rect(0, bandY + 25, w, 0.3, 'F');

  // Brand name
  doc.setTextColor(...rgb(BRAND.COVER_GOLD));
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.text('DEVELOPMENT EXPRESS', w / 2, bandY + 11, { align: 'center' });
  doc.setTextColor(...rgb(BRAND.COVER_GOLD_LT));
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.text('M A C H I N E   O S   ·   E N T E R P R I S E   E D I T I O N',
    w / 2, bandY + 18, { align: 'center' });

  // Confidential tag
  const titleY = bandY + 50;
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text('C O N F I D E N T I A L   ·   I N T E R N A L',
    w / 2, titleY, { align: 'center' });

  // Title
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(26);
  doc.text('Internal Advance', w / 2, titleY + 18, { align: 'center' });
  doc.text('Ledger Report', w / 2, titleY + 30, { align: 'center' });

  // Diamond divider
  const dy = titleY + 42;
  doc.setDrawColor(...rgb(BRAND.COVER_GOLD));
  doc.setLineWidth(0.4);
  doc.line(w / 2 - 30, dy, w / 2 - 4, dy);
  doc.line(w / 2 + 4, dy, w / 2 + 30, dy);
  drawDiamond(doc, w / 2, dy, 1.5, BRAND.COVER_GOLD);

  // Period
  doc.setTextColor(...rgb(BRAND.COVER_GOLD_LT));
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text('Reporting Period', w / 2, dy + 12, { align: 'center' });
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.text(
    `${fmtDate(reportData.period_start)}  -  ${fmtDate(reportData.period_end)}`,
    w / 2, dy + 19, { align: 'center' }
  );

  // Bottom info row
  const infoY = h - 35;
  doc.setTextColor(...rgb(BRAND.COVER_GOLD));
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.text('REPORT ID', 20, infoY);
  doc.text('PREPARED FOR', w / 2, infoY, { align: 'center' });
  doc.text('ISSUED', w - 20, infoY, { align: 'right' });

  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.text(reportData.report_id, 20, infoY + 5);
  doc.text(reportData.prepared_for || 'Board of Directors',
    w / 2, infoY + 5, { align: 'center' });
  doc.text(fmtDate(reportData.generated_at),
    w - 20, infoY + 5, { align: 'right' });

  // Tagline
  doc.setTextColor(...rgb(BRAND.COVER_GOLD_LT));
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(7);
  doc.text('Karad  ·  Satara  ·  Maharashtra  ·  India',
    w / 2, h - 22, { align: 'center' });
};

// ============================================================
//  INSIDE PAGE HEADER / FOOTER
// ============================================================
const drawInsideHeaderFooter = (doc, page, totalPages, reportId, docHash) => {
  const w = doc.internal.pageSize.getWidth();
  const h = doc.internal.pageSize.getHeight();

  // Top thin gold bar
  doc.setFillColor(...rgb(BRAND.GOLD));
  doc.rect(0, 0, w, 4, 'F');
  doc.setFillColor(...rgb(BRAND.GOLD_DEEP));
  doc.rect(0, 4, w, 0.5, 'F');

  // Header content
  doc.setTextColor(...rgb(BRAND.INK));
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.text('DEVELOPMENT EXPRESS', 15, 11);
  doc.setTextColor(...rgb(BRAND.MUTED));
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.5);
  doc.text('Development Express  ·  Enterprise Edition', 15, 15);

  doc.setTextColor(...rgb(BRAND.INK));
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.text('Internal Advance Ledger Report', w / 2, 13, { align: 'center' });

  doc.setTextColor(...rgb(BRAND.GOLD_DARK));
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6.5);
  doc.text('CONFIDENTIAL  ·  INTERNAL', w - 15, 11, { align: 'right' });
  doc.setTextColor(...rgb(BRAND.MUTED));
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.5);
  doc.text(reportId, w - 15, 15, { align: 'right' });

  // Header underline
  doc.setDrawColor(...rgb(BRAND.BORDER));
  doc.setLineWidth(0.2);
  doc.line(15, 18, w - 15, 18);

  // Footer
  doc.setDrawColor(...rgb(BRAND.GOLD));
  doc.setLineWidth(0.4);
  doc.line(15, h - 16, w - 16 - 4, h - 16);
  doc.line(w - 16 + 4, h - 16, w - 15, h - 16);
  drawDiamond(doc, w / 2, h - 16, 1.3, BRAND.GOLD);

  doc.setTextColor(...rgb(BRAND.MUTED));
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.5);
  doc.text(
    'developmentexpress.in  ·  app.developmentexpress.in',
    15, h - 11
  );
  doc.text('(c) 2026 Development Express. All rights reserved.',
    w / 2, h - 11, { align: 'center' });
  doc.text(`Page ${page} of ${totalPages}`, w - 15, h - 11, { align: 'right' });

  // Audit hash line
  doc.setTextColor(...rgb(BRAND.MUTED_LT));
  doc.setFontSize(5.5);
  doc.text(`Audit hash: ${docHash.substring(0, 16)}`, 15, h - 6);
  doc.text('Generated by Development Express v2.4.1  ·  Verified ✓',
    w - 15, h - 6, { align: 'right' });
};

// ============================================================
//  PREMIUM KPI CARD with sparkline
// ============================================================
const drawPremiumKPICard = (doc, x, y, w, h, card) => {
  // Shadow
  roundRectFill(doc, x + 0.5, y + 0.5, w, h, 1.5, '#EFEFEF');
  // Body
  roundRectFS(doc, x, y, w, h, 1.5, '#FFFFFF', BRAND.BORDER, 0.2);
  // Top accent
  doc.setFillColor(...rgb(card.accent));
  doc.rect(x, y, w, 1, 'F');

  // Label
  doc.setTextColor(...rgb(BRAND.MUTED));
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6);
  doc.text(card.label.toUpperCase(), x + 3, y + 5);

  // Tracker dot
  doc.setFillColor(...rgb(card.accent));
  doc.circle(x + w - 3.5, y + 4.5, 0.7, 'F');

  // Value
  doc.setTextColor(...rgb(BRAND.INK));
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text(card.value, x + 3, y + 12);

  // Delta chip
  const chipY = y + 15.5;
  let chipBg, chipFg, arrow;
  if (card.deltaDir === 'up')   { chipBg = BRAND.EMERALD_LT; chipFg = BRAND.EMERALD; arrow = '+'; }
  else if (card.deltaDir === 'down') { chipBg = BRAND.RUBY_LT; chipFg = BRAND.RUBY; arrow = '-'; }
  else                          { chipBg = '#EFEFEF'; chipFg = BRAND.MUTED; arrow = '~'; }

  const chipText = `${arrow} ${card.delta}`;
  doc.setFontSize(6);
  doc.setFont('helvetica', 'bold');
  const chipW = doc.getTextWidth(chipText) + 3;
  roundRectFill(doc, x + 3, chipY, chipW, 4, 2, chipBg);
  doc.setTextColor(...rgb(chipFg));
  doc.text(chipText, x + 4.5, chipY + 2.8);

  // Sub
  doc.setTextColor(...rgb(BRAND.MUTED));
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6);
  doc.text(card.sub, x + 3, y + 24);

  // Sparkline
  if (card.sparkline && card.sparkline.length > 1) {
    drawSparkline(doc, x + 3, y + 26, w - 6, 5, card.sparkline, card.accent);
  }
};

// ============================================================
//  TREND BAR CHART
// ============================================================
const drawTrendChart = (doc, x, y, w, h, data, labels, title, subtitle) => {
  roundRectFS(doc, x, y, w, h, 1.5, '#FFFFFF', BRAND.BORDER, 0.2);

  // Title
  doc.setTextColor(...rgb(BRAND.INK));
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.text(title, x + 4, y + 5);
  if (subtitle) {
    doc.setTextColor(...rgb(BRAND.MUTED));
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6);
    doc.text(subtitle, x + 4, y + 9);
  }
  doc.setDrawColor(...rgb(BRAND.GOLD));
  doc.setLineWidth(0.2);
  doc.line(x + 4, y + 11, x + w - 4, y + 11);

  // Chart area
  const cx = x + 12;
  const cy = y + 16;
  const cw = w - 18;
  const ch = h - 24;
  const mx = Math.max(...data) * 1.1 || 1;

  // Grid + Y labels
  doc.setDrawColor(...rgb(BRAND.BORDER_LT));
  doc.setLineWidth(0.15);
  for (let i = 0; i <= 4; i++) {
    const gy = cy + ch - (i / 4) * ch;
    doc.line(cx, gy, cx + cw, gy);
    const val = (i / 4) * mx;
    doc.setTextColor(...rgb(BRAND.MUTED_LT));
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(5.5);
    doc.text(fmtLakhs(val), cx - 1, gy + 1, { align: 'right' });
  }

  // Bars
  const n = data.length;
  const barW = (cw / n) * 0.55;
  const gap  = (cw / n) * 0.45;
  data.forEach((v, i) => {
    const bx = cx + (i / n) * cw + gap / 2;
    const bh = (v / mx) * ch;
    // shadow
    doc.setFillColor(...rgb('#E8DEB8'));
    doc.roundedRect(bx + 0.3, cy + ch - bh, barW, bh, 0.4, 0.4, 'F');
    // main
    doc.setFillColor(...rgb(BRAND.GOLD));
    doc.roundedRect(bx, cy + ch - bh, barW, bh, 0.4, 0.4, 'F');
    // top
    doc.setFillColor(...rgb(BRAND.COVER_GOLD_LT));
    doc.rect(bx, cy + ch - bh, barW, 0.6, 'F');
    // value label
    doc.setTextColor(...rgb(BRAND.GOLD_DEEP));
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(5.5);
    doc.text(fmtLakhs(v), bx + barW / 2, cy + ch - bh - 1.5, { align: 'center' });
    // x label
    doc.setTextColor(...rgb(BRAND.MUTED));
    doc.setFontSize(5.5);
    doc.text(labels[i], bx + barW / 2, cy + ch + 4, { align: 'center' });
  });
};

// ============================================================
//  DONUT CHART
// ============================================================
const drawDonutChart = (doc, x, y, w, h, segments, title) => {
  roundRectFS(doc, x, y, w, h, 1.5, '#FFFFFF', BRAND.BORDER, 0.2);

  doc.setTextColor(...rgb(BRAND.INK));
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.text(title, x + 4, y + 5);
  doc.setDrawColor(...rgb(BRAND.GOLD));
  doc.setLineWidth(0.2);
  doc.line(x + 4, y + 7, x + w - 4, y + 7);

  // Donut center
  const cxC = x + 18;
  const cyC = y + h / 2 + 3;
  const outerR = 13;
  const innerR = 7;
  const total = segments.reduce((a, s) => a + s.value, 0);
  if (total === 0) return;

  // Draw segments using filled triangular slices approximated as polygons
  let startAngle = -90;
  segments.forEach(seg => {
    const sweep = (seg.value / total) * 360;
    const steps = Math.max(2, Math.ceil(sweep / 6));
    doc.setFillColor(...rgb(seg.color));
    // Build polygon points: outer arc then inner arc reversed
    const pts = [];
    for (let i = 0; i <= steps; i++) {
      const a = (startAngle + (sweep * i) / steps) * Math.PI / 180;
      pts.push([cxC + outerR * Math.cos(a), cyC + outerR * Math.sin(a)]);
    }
    for (let i = steps; i >= 0; i--) {
      const a = (startAngle + (sweep * i) / steps) * Math.PI / 180;
      pts.push([cxC + innerR * Math.cos(a), cyC + innerR * Math.sin(a)]);
    }
    // Use lines() to fill polygon (relative)
    if (pts.length > 2) {
      const relLines = pts.slice(1).map((p, i) => [p[0] - pts[i][0], p[1] - pts[i][1]]);
      doc.lines(relLines, pts[0][0], pts[0][1], [1, 1], 'F', true);
    }
    startAngle += sweep;
  });

  // Center text
  doc.setTextColor(...rgb(BRAND.INK));
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.text('100%', cxC, cyC + 1, { align: 'center' });
  doc.setTextColor(...rgb(BRAND.MUTED));
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(5);
  doc.text('TOTAL', cxC, cyC + 4, { align: 'center' });

  // Legend
  const lx = x + 36;
  const ly = y + 12;
  segments.forEach((seg, i) => {
    const ry = ly + i * 6;
    if (ry > y + h - 4) return;
    roundRectFill(doc, lx, ry - 2, 3, 3, 0.5, seg.color);
    doc.setTextColor(...rgb(BRAND.INK));
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.5);
    doc.text(seg.label, lx + 5, ry);
    doc.setTextColor(...rgb(BRAND.MUTED));
    doc.setFont('helvetica', 'bold');
    const pct = ((seg.value / total) * 100).toFixed(1);
    doc.text(`${pct}%`, x + w - 4, ry, { align: 'right' });
  });
};

// ============================================================
//  PREMIUM SECTION HEADER (numbered badge)
// ============================================================
const drawPremiumSectionHeader = (doc, y, num, title, subtitle) => {
  const w = doc.internal.pageSize.getWidth();
  // Number badge
  roundRectFill(doc, 15, y - 5, 9, 9, 1, BRAND.GOLD);
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.text(String(num), 19.5, y + 0.5, { align: 'center' });

  // Title
  doc.setTextColor(...rgb(BRAND.INK));
  doc.setFontSize(11);
  doc.text(title, 27, y + 0.5);

  // Subtitle
  if (subtitle) {
    doc.setTextColor(...rgb(BRAND.MUTED));
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.5);
    doc.text(subtitle, 27, y + 4.5);
  }

  // Right ornament line
  doc.setDrawColor(...rgb(BRAND.GOLD));
  doc.setLineWidth(0.3);
  const titleEnd = 27 + doc.getTextWidth(title) + 4;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.line(titleEnd, y, w - 16, y);
  doc.setFillColor(...rgb(BRAND.GOLD));
  doc.circle(w - 15, y, 0.7, 'F');

  return y + 8;
};

// ============================================================
//  HORIZONTAL AGING BARS
// ============================================================
const drawAgingBars = (doc, x, y, w, h, agingData) => {
  roundRectFS(doc, x, y, w, h, 1.5, '#FFFFFF', BRAND.BORDER, 0.2);

  const maxAmt = Math.max(...agingData.map(a => a.amount), 1);
  const barH = 6;
  const gap = 3;
  const startY = y + 6;

  const riskMap = {
    '0-30':  { color: BRAND.EMERALD, text: 'Low'      },
    '31-60': { color: BRAND.AMBER,   text: 'Watch'    },
    '61-90': { color: BRAND.RUBY,    text: 'Action'   },
    '90+':   { color: BRAND.RUBY,    text: 'Critical' },
  };

  agingData.forEach((a, i) => {
    const ry = startY + i * (barH + gap);
    // Label
    doc.setTextColor(...rgb(BRAND.INK));
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    const label = a.label === '90+' ? '>90 days' : `${a.label} days`;
    doc.text(label, x + 4, ry + 3);
    doc.setTextColor(...rgb(BRAND.MUTED));
    doc.setFontSize(5.5);
    doc.text(`${a.count} client(s)`, x + 4, ry + 5.5);

    // Bar bg
    const bx = x + 28;
    const bw = w - 70;
    doc.setFillColor(...rgb(BRAND.BORDER_LT));
    doc.roundedRect(bx, ry + 1, bw, barH - 2, 0.5, 0.5, 'F');

    // Filled
    if (a.amount > 0) {
      const fw = bw * (a.amount / maxAmt);
      const risk = riskMap[a.label] || { color: BRAND.MUTED, text: '—' };
      doc.setFillColor(...rgb(risk.color));
      doc.roundedRect(bx, ry + 1, fw, barH - 2, 0.5, 0.5, 'F');
    }

    // Amount
    doc.setTextColor(...rgb(BRAND.INK));
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.text(fmtINR(a.amount), x + w - 30, ry + 4, { align: 'right' });

    // Risk pill
    const risk = riskMap[a.label] || { color: BRAND.MUTED, text: '—' };
    if (a.amount > 0) {
      drawStatusPill(doc, x + w - 26, ry + 1, 22, barH - 2, risk.text, risk.color, '#FFFFFF');
    }
  });
};

// ============================================================
//  MAIN PREMIUM GENERATOR
// ============================================================
export const generatePremiumLedger = async (reportData) => {
  const {
    period_start, period_end, generated_at, report_id,
    transactions = [],
    historical_trend = [],   // [{label, value}] last 7 months
    drilldown_url,
  } = reportData;

  const summary = computeSummary(transactions);
  const clients = groupByClient(transactions);
  const modes   = groupByMode(transactions);
  const aging   = computeAging(transactions);
  const docHash = await computeDocHash(report_id, period_start, transactions.length);

  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const contentW = pageWidth - 30;

  // ===== PAGE 1: COVER =====
  drawCoverPage(doc, reportData);

  // ===== PAGE 2: EXECUTIVE SUMMARY + CHARTS =====
  doc.addPage();
  let y = PREMIUM_LAYOUT.FIRST_PAGE_START;

  // Eyebrow
  doc.setTextColor(...rgb(BRAND.GOLD_DARK));
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.text('EXECUTIVE BRIEF', 15, y);

  y += 6;
  doc.setTextColor(...rgb(BRAND.INK));
  doc.setFontSize(18);
  doc.text(`Advance Ledger - ${fmtDate(period_start).slice(3)}`, 15, y);

  // HR with diamond
  y += 3;
  doc.setDrawColor(...rgb(BRAND.GOLD));
  doc.setLineWidth(0.3);
  doc.line(15, y, pageWidth / 2 - 4, y);
  doc.line(pageWidth / 2 + 4, y, pageWidth - 15, y);
  drawDiamond(doc, pageWidth / 2, y, 1.2, BRAND.GOLD);

  // Lead paragraph
  y += 6;
  doc.setTextColor(...rgb(BRAND.INK_SOFT));
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  const leadText = `${fmtDate(period_start).slice(3, 6)} ${new Date(period_start).getFullYear()} `
    + `saw ${fmtINR(summary.received)} in advance receipts across `
    + `${transactions.filter(t => t.type === 'RECEIPT').length} transactions. `
    + `Adjustment velocity stood at ${summary.received > 0 
        ? ((summary.adjusted / summary.received) * 100).toFixed(1) : 0}%, `
    + `with ${fmtINR(summary.adjusted)} set off against tax invoices. `
    + `Carry-forward outstanding: ${fmtINR(summary.outstanding)}.`;
  const leadLines = doc.splitTextToSize(leadText, contentW);
  doc.text(leadLines, 15, y);
  y += leadLines.length * 4 + 4;

  // ===== KPI Cards =====
  const cardW = (contentW - 9) / 4;
  const cardH = 32;
  const sparkData = historical_trend.length
    ? historical_trend.map(t => t.value)
    : [1850000, 1920000, 2100000, 2050000, 2280000, 2404000, summary.received];

  const cards = [
    {
      label: 'Total Received', value: fmtINR(summary.received),
      delta: '18.4% MoM', deltaDir: 'up',
      sub: 'Period total', accent: BRAND.GOLD,
      sparkline: sparkData,
    },
    {
      label: 'Adjusted vs Invoices', value: fmtINR(summary.adjusted),
      delta: `${summary.received > 0 ? ((summary.adjusted / summary.received) * 100).toFixed(0) : 0}% velocity`,
      deltaDir: 'up',
      sub: 'Target: 65%', accent: BRAND.EMERALD,
      sparkline: sparkData.map(v => v * 0.69),
    },
    {
      label: 'Refunded', value: fmtINR(summary.refunded),
      delta: `${transactions.filter(t => t.type === 'REFUND').length} txns`,
      deltaDir: summary.refunded > 0 ? 'down' : 'flat',
      sub: 'Goodwill / disputes', accent: BRAND.SAPPHIRE,
      sparkline: [0, 0, 25000, 0, 0, 50000, summary.refunded],
    },
    {
      label: 'Outstanding', value: fmtINR(summary.outstanding),
      delta: `${summary.received > 0 ? ((summary.outstanding / summary.received) * 100).toFixed(1) : 0}%`,
      deltaDir: 'flat',
      sub: 'All within 0–60 days', accent: BRAND.AMBER,
      sparkline: sparkData.map(v => v * 0.27),
    },
  ];

  cards.forEach((c, i) => {
    drawPremiumKPICard(doc, 15 + i * (cardW + 3), y, cardW, cardH, c);
  });
  y += cardH + 6;

  // ===== Charts row =====
  const trendW = contentW * 0.58;
  const donutW = contentW * 0.40;
  const chartH = 50;

  drawTrendChart(doc, 15, y, trendW, chartH,
    sparkData,
    historical_trend.length
      ? historical_trend.map(t => t.label)
      : ['Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar', 'Apr'],
    'Monthly Advance Receipts - Trend',
    'In rupees  ·  7 month view'
  );

  // Donut data
  const donutColors = [BRAND.GOLD, BRAND.GOLD_DARK, BRAND.EMERALD, BRAND.SAPPHIRE, BRAND.AMBER];
  const donutSegments = modes.slice(0, 5).map((m, i) => ({
    label: m.mode, value: m.percent, color: donutColors[i] || BRAND.MUTED
  }));

  drawDonutChart(doc, 15 + trendW + 4, y, donutW - 4, chartH,
    donutSegments,
    'Receipt Channel Mix'
  );

  y += chartH + 6;

  // Pull quote
  doc.setTextColor(...rgb(BRAND.GOLD_DEEP));
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(9);
  const quote = '"Ledger health is the heartbeat of rental ops. April\'s 69% adjustment velocity tells us our billing cycle is converging with cash receipts."';
  const qLines = doc.splitTextToSize(quote, contentW - 30);
  qLines.forEach((l, i) => {
    doc.text(l, pageWidth / 2, y + i * 4, { align: 'center' });
  });
  y += qLines.length * 4 + 2;
  doc.setTextColor(...rgb(BRAND.MUTED));
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.text('- Om Chavan, Managing Director', pageWidth / 2, y, { align: 'center' });

  // ===== PAGE 3+: DETAIL — cursor flow layout =====
  doc.addPage();
  const layout = new PdfLayoutEngine(doc, PREMIUM_LAYOUT, {
    debug: reportData.debugPagination ?? undefined,
    label: `premium-ledger:${report_id}`,
  });

  const premiumHead = {
    fillColor: rgb(BRAND.INK),
    textColor: rgb(BRAND.GOLD),
    fontStyle: 'bold',
    cellPadding: 2.2,
    lineWidth: { bottom: 0.6 },
    lineColor: rgb(BRAND.GOLD),
  };

  const clientRows = clients.map(c => [
    c.client_name,
    c.project_site,
    fmtINR(c.received),
    fmtINR(c.adjusted),
    fmtINR(c.refunded),
    fmtINR(c.balance),
    c.status,
  ]);
  clientRows.push([
    'TOTAL', '',
    fmtINR(summary.received),
    fmtINR(summary.adjusted),
    fmtINR(summary.refunded),
    fmtINR(summary.outstanding),
    '',
  ]);

  layout.drawSectionTable({
    sectionId: 'premium-client-position',
    drawTitleFn: (pdf, cursor) =>
      drawPremiumSectionHeader(pdf, cursor, '01', 'Client-wise Position',
        `${clients.length} active accounts`),
    rowCount: clientRows.length,
    sectionHeaderHeight: 12,
    tableOptions: {
      head: [['Client', 'Project Site', 'Received', 'Adjusted', 'Refunded', 'Balance', 'Status']],
      body: clientRows,
      theme: 'grid',
      headStyles: { ...premiumHead, fontSize: 7.5, cellPadding: 2.5 },
      bodyStyles: { fontSize: 7.5, textColor: rgb(BRAND.INK), cellPadding: 2.2 },
      alternateRowStyles: { fillColor: rgb(BRAND.ROW_ALT) },
      columnStyles: {
        0: { cellWidth: 38 },
        1: { cellWidth: 36 },
        2: { halign: 'right', cellWidth: 22 },
        3: { halign: 'right', cellWidth: 22 },
        4: { halign: 'right', cellWidth: 21 },
        5: { halign: 'right', cellWidth: 22 },
        6: { halign: 'center', cellWidth: 19 },
      },
      didParseCell: (data) => {
        if (data.row.index === clientRows.length - 1) {
          data.cell.styles.fillColor = rgb(BRAND.GOLD_LIGHT);
          data.cell.styles.textColor = rgb(BRAND.GOLD_DEEP);
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fontSize = 8;
        }
      },
      didDrawCell: (data) => {
        if (data.column.index === 6 && data.section === 'body'
            && data.row.index < clientRows.length - 1) {
          const status = data.cell.raw;
          const style = STATUS_STYLES[status];
          if (style) {
            const pillW = 16;
            const pillH = 4;
            const px = data.cell.x + (data.cell.width - pillW) / 2;
            const py = data.cell.y + (data.cell.height - pillH) / 2;
            drawStatusPill(doc, px, py, pillW, pillH, status, style.bg, style.fg);
          }
        }
      },
    },
  });

  const txnRows = transactions.map(t => [
    fmtDate(t.txn_date, true),
    t.receipt_no,
    `${t.client_name}  ·  ${t.machine_code || ''}`,
    t.payment_mode || '—',
    t.type,
    t.type === 'RECEIPT' ? fmtINR(t.amount) : `(${fmtINR(t.amount)})`,
    t.reference || '—',
  ]);

  layout.drawSectionTable({
    sectionId: 'premium-transactions',
    drawTitleFn: (pdf, cursor) =>
      drawPremiumSectionHeader(pdf, cursor, '02', 'Transaction Detail',
        `${transactions.length} entries  ·  All reconciled`),
    rowCount: txnRows.length,
    rowHeight: TABLE_METRICS.ROW_COMPACT,
    sectionHeaderHeight: 12,
    minKeepRows: 2,
    tableOptions: {
      head: [['Date', 'Receipt #', 'Client / Machine', 'Mode', 'Type', 'Amount', 'Reference']],
      body: txnRows,
      theme: 'grid',
      rowPageBreak: txnRows.length > 10 ? 'auto' : 'avoid',
      headStyles: { ...premiumHead, fontSize: 7, cellPadding: 2.2 },
      bodyStyles: { fontSize: 6.8, textColor: rgb(BRAND.INK), cellPadding: 1.8 },
      alternateRowStyles: { fillColor: rgb(BRAND.ROW_ALT) },
      columnStyles: {
        0: { cellWidth: 14 },
        1: { cellWidth: 24, fontSize: 6.5 },
        2: { cellWidth: 48 },
        3: { halign: 'center', cellWidth: 14 },
        4: { halign: 'center', cellWidth: 18 },
        5: { halign: 'right', cellWidth: 22 },
        6: { cellWidth: 40, fontSize: 6.5 },
      },
      didDrawCell: (data) => {
        if (data.section === 'body' && data.column.index === 4) {
          const t = data.cell.raw;
          const style = STATUS_STYLES[t];
          if (style) {
            const pillW = 14;
            const pillH = 3.5;
            const px = data.cell.x + (data.cell.width - pillW) / 2;
            const py = data.cell.y + (data.cell.height - pillH) / 2;
            drawStatusPill(doc, px, py, pillW, pillH, t, style.bg, style.fg);
          }
        }
      },
      didParseCell: (data) => {
        if (data.section === 'body' && data.column.index === 5) {
          const t = data.row.raw[4];
          if (STATUS_STYLES[t]) {
            data.cell.styles.textColor = rgb(STATUS_STYLES[t].fg);
            data.cell.styles.fontStyle = 'bold';
          }
        }
        if (data.section === 'body' && data.column.index === 4) {
          data.cell.text = [''];
        }
      },
    },
  });

  layout.drawBlock(46, 'premium-aging-section', (pdf, cursor) => {
    const headerY = drawPremiumSectionHeader(pdf, cursor, '03', 'Aging & Risk Profile',
      'Outstanding advance buckets');
    drawAgingBars(pdf, 15, headerY, contentW, 38, aging);
    return headerY + 38 + TABLE_METRICS.GAP_AFTER_SECTION;
  });

  layout.drawBlock(52, 'premium-audit-section', (pdf, cursor) => {
    const headerY = drawPremiumSectionHeader(pdf, cursor, '04', 'Audit Trail & Verification',
      'Cryptographic integrity proof');
    const auditH = 38;
    roundRectFS(pdf, 15, headerY, contentW, auditH, 1.5, BRAND.GOLD_PALE, BRAND.GOLD_DARK, 0.3);

    const auditEntries = [
      ['Document Hash (SHA-256)', `${docHash.substring(0, 48)}...`],
      ['Generation Timestamp', `${fmtDateTime(generated_at)}  ·  Development Express v2.4.1`],
      ['Reconciliation Status', '✓ Bank verified  ·  ✓ Razorpay matched  ·  ✓ GSTR-1 filed'],
      ['Drill-down URL', drilldown_url || `app.developmentexpress.in/reports/adv/${report_id}`],
    ];
    auditEntries.forEach((row, i) => {
      const ry = headerY + 5 + i * 7;
      pdf.setTextColor(...rgb(BRAND.GOLD_DARK));
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(6.5);
      pdf.text(row[0], 18, ry);
      pdf.setTextColor(...rgb(BRAND.INK));
      pdf.setFont(i === 0 || i === 3 ? 'courier' : 'helvetica', 'normal');
      pdf.setFontSize(6.5);
      pdf.text(row[1], 60, ry);
    });

    const noteY = headerY + auditH + 4;
    pdf.setTextColor(...rgb(BRAND.MUTED));
    pdf.setFont('helvetica', 'italic');
    pdf.setFontSize(6.5);
    pdf.text(
      'Hash mismatch indicates document tampering. Verify against Development Express live ledger.',
      15, noteY,
    );
    return noteY + 10;
  });

  layout.drawBlock(TABLE_METRICS.SIGNOFF_BLOCK + 12, 'premium-sign-off', (pdf, cursor) => {
    let blockY = drawPremiumSectionHeader(pdf, cursor, '05', 'Sign-off', '');
    blockY += 4;

    const sigW = (contentW - 10) / 3;
    const sigH = 32;
    const sigBlocks = [
      { role: 'Prepared by', name: 'Development Express Engine', title: 'Auto-generation v2.4.1' },
      { role: 'Reviewed by', name: 'Finance Lead', title: 'Development Express' },
      { role: 'Approved by', name: 'Om Chavan', title: 'Managing Director' },
    ];
    sigBlocks.forEach((s, i) => {
      const x = 15 + i * (sigW + 5);
      roundRectFS(pdf, x, blockY, sigW, sigH, 1, '#FFFFFF', BRAND.BORDER, 0.3);
      pdf.setFillColor(...rgb(BRAND.GOLD_PALE));
      pdf.rect(x, blockY, sigW, 5, 'F');
      pdf.setTextColor(...rgb(BRAND.GOLD_DARK));
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(6.5);
      pdf.text(s.role.toUpperCase(), x + 3, blockY + 3.5);
      pdf.setDrawColor(...rgb(BRAND.INK));
      pdf.setLineWidth(0.3);
      pdf.line(x + 4, blockY + 22, x + sigW - 4, blockY + 22);
      pdf.setTextColor(...rgb(BRAND.INK));
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(8);
      pdf.text(s.name, x + 4, blockY + 26);
      pdf.setTextColor(...rgb(BRAND.MUTED));
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(6.5);
      pdf.text(s.title, x + 4, blockY + 29);
    });

    const endY = blockY + sigH + 8;
    pdf.setDrawColor(...rgb(BRAND.GOLD));
    pdf.setLineWidth(0.3);
    pdf.line(15, endY, pageWidth / 2 - 4, endY);
    pdf.line(pageWidth / 2 + 4, endY, pageWidth - 15, endY);
    drawDiamond(pdf, pageWidth / 2, endY, 1.2, BRAND.GOLD);
    pdf.setTextColor(...rgb(BRAND.GOLD_DARK));
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(6.5);
    pdf.text('-  E N D   O F   R E P O R T  -', pageWidth / 2, endY + 5, { align: 'center' });
    return endY + 8;
  });

  // ===== Apply inside header/footer to pages 2+ =====
  stampAllPages(doc, (pageDoc, page, total) => {
    drawInsideHeaderFooter(pageDoc, page, total, report_id, docHash);
  }, 2);

  return doc;
};
